import PeopleIcon from "@mui/icons-material/People";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import { ComponentType } from "react";
import { SvgIconProps } from "@mui/material";
import WalkthroughIcon from "../../../../common/svgs/WalkthroughIcon";
// Sidebar glyphs from the new-platform design (Figma node 16306:68615). The
// same four are used by both single-subject rails — the affiliate's client
// workspace and the customer's own workspace.
import GridLayoutIcon from "../../../../common/svgs/GridLayoutIcon";
import PulseIcon from "../../../../common/svgs/PulseIcon";
import SquareCheckIcon from "../../../../common/svgs/SquareCheckIcon";
import ProfileIcon from "../../../../common/svgs/ProfileIcon";
import HistoryIcon from "../../../../common/svgs/HistoryIcon";
export type NavItem = {
  id: string;
  label: string;
  // Accepts both MUI icons (PeopleIcon, …) and custom SvgIcon-based glyphs.
  icon?: ComponentType<SvgIconProps>;
  link?: string;
  children?: NavItem[];
  role?: 1 | 2;
  autoExpand?: boolean; // Auto-controlled by selected client state
  hideInSidebar?: boolean; // Hide from sidebar navigation
};

// Helper function to find nav item by path segment
const findNavItem = (
  structure: NavItem[],
  segment: string,
  skipNavItems: string[]
): NavItem | null => {
  for (const item of structure) {
    // Search children first to find the most specific match
    if (item.children) {
      const found = findNavItem(item.children, segment, skipNavItems);
      if (found) return found;
    }

    // Only match if this item is not in skipNavItems
    const itemSegment = item.link?.split("/").pop()?.split("?")[0];
    if (itemSegment === segment && !skipNavItems.includes(item.id)) {
      return item;
    }
  }
  return null;
};

// Generate breadcrumb items from current path
// Routes whose deeper URL segments are tabs within a single page, not separate
// detail records. They get no trailing "… Details" crumb (see below).
const TAB_ROUTES = ["/client/profile", "/platform/profile"];

// History is one page per shell with an in-page tab strip. Its trail is built
// explicitly rather than through `findNavItem`, which resolves by *last path
// segment* across the combined tree — `/client/history-list/programming` and
// `/client/history/programming` share the segment "programming", so the generic
// path would give the affiliate page a crumb linking into the client workspace.
const HISTORY_BASES = [
  "/client/history-list",
  "/client/history",
  "/platform/history",
];

const HISTORY_TAB_LABELS: Record<string, string> = {
  assessments: "Assessments",
  programming: "Programming",
  prescription: "Prescription",
  timeline: "Timeline",
};

export const getBreadcrumbItems = (
  pathname: string,
  isSimpleUser: boolean
): { label: string; link?: string }[] => {
  const pathSegments = pathname.split("/").filter(Boolean);
  const items: { label: string; link?: string }[] = [];

  // Skip path segments that shouldn't be processed
  const skipSegments = ["client", "platform"];
  // Skip grouping nav items that shouldn't appear in breadcrumb (but process their children)
  const skipNavItems = ["client-info", "programming", "services"];

  // History: "History › <tab>", and nothing deeper. Detail pages are reached by
  // clicking a row and carry their own Back link, so a third crumb would only
  // restate the tab.
  const historyBase = HISTORY_BASES.find(
    (base) => pathname === base || pathname.startsWith(`${base}/`)
  );
  if (historyBase) {
    items.push({ label: "History", link: historyBase });
    const tabSegment = pathname.slice(historyBase.length).split("/")[1];
    const tabLabel = tabSegment ? HISTORY_TAB_LABELS[tabSegment] : undefined;
    if (tabLabel) {
      items.push({ label: tabLabel, link: `${historyBase}/${tabSegment}` });
    }
    return items;
  }

  // Check if we're on a client-related page (not services)
  const isClientPage = pathSegments[0] === "client" && !isSimpleUser;
  const isClientListPage = pathname === "/client/client-list";
  const isServicesPage = pathname.startsWith("/client/services");
  const isOrdersPage = pathname.startsWith("/client/orders");
  const isSettingsPage = pathname === "/client/settings";
  const isWalkthroughsPage = pathname.startsWith("/client/walkthroughs");

  // Add "Clients" as first breadcrumb item for client-related pages (except client list, services, settings, and walkthroughs)
  if (
    isClientPage &&
    !isClientListPage &&
    !isServicesPage &&
    !isSettingsPage &&
    !isOrdersPage &&
    !isWalkthroughsPage
  ) {
    items.push({
      label: "Clients",
      link: "/client/client-list",
    });
  }

  // For services pages, only show the deepest/most specific page
  if (isServicesPage) {
    let deepestNavItem = null;
    for (const segment of pathSegments) {
      if (skipSegments.includes(segment)) continue;
      const navItem = findNavItem(navigationStructure, segment, skipNavItems);
      if (navItem) {
        deepestNavItem = navItem;
      }
    }

    if (deepestNavItem && deepestNavItem.link) {
      items.push({
        label: deepestNavItem.label,
        link: deepestNavItem.link,
      });
    }
  } else {
    // For other pages, show the full hierarchy
    for (const segment of pathSegments) {
      // Skip the base path segments only
      if (skipSegments.includes(segment)) {
        continue;
      }

      const navItem = findNavItem(navigationStructure, segment, skipNavItems);

      if (navItem && navItem.link) {
        items.push({
          label: navItem.label,
          link: navItem.link,
        });
      }
    }
  }

  // Add detail breadcrumb for prescription and logic detail pages
  const lastItem = items[items.length - 1];
  if (lastItem) {
    // Check if we're on a detail page (URL has additional segments after the base path)
    // — except for routes whose deeper segments are TABS rather than detail
    // records. The tab strip already shows which one is open, so "Profile
    // Details" would just restate the page with less information.
    const isDetailPage =
      !!lastItem.link &&
      pathname.startsWith(lastItem.link + "/") &&
      !TAB_ROUTES.includes(lastItem.link);

    if (isDetailPage) {
      // Map parent labels to their detail label
      // "Logic Builder" → "Logic Detail" (not "Logic Builder Detail")
      const parentLabel =
        lastItem.label === "Logic Builder" ? "Logic" : lastItem.label;

      // Orders hosts two listings behind a tab strip. The PNOĒ branch reads as
      // "Test Details"; a lab order still reads "Orders Details".
      const detailLabel = pathname.startsWith("/client/orders/pnoe-test/")
        ? "Test Details"
        : `${parentLabel} Details`;

      items.push({
        label: detailLabel,
        link: "#", // Current page, no navigation
      });
    }
  }

  return items;
};

// The client workspace pages — rendered in a dedicated browser tab per client
// (the ClientWorkspace shell). One client per tab; the client id travels in
// the `?id=` query param and is stamped onto these links by the sidebar.
export const clientWorkspaceNavigation: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: GridLayoutIcon,
    link: "/client/dashboard",
  },
  {
    id: "score",
    label: "Health overview",
    icon: PulseIcon,
    link: "/client/score",
  },
  {
    id: "programming",
    label: "Programming",
    icon: SquareCheckIcon,
    link: "/client/programming/nutrition",
    children: [
      {
        id: "nutrition-plans",
        label: "Nutrition",
        link: "/client/programming/nutrition",
      },
      {
        id: "workout",
        label: "Workout",
        link: "/client/programming/workout",
      },
      {
        id: "bio-optimization",
        label: "Bio Optimization",
        link: "/client/programming/bio-optimization",
      },
    ],
  },
  {
    // Absorbed the old Client info › Prescription tab, plus the two Orders
    // listings from the affiliate rail. One client's history — the affiliate's
    // cross-client view is a separate page at /client/history-list.
    id: "client-history",
    label: "History",
    icon: HistoryIcon,
    link: "/client/history",
  },
  {
    // Last in the rail, per the design. Flat on purpose: the three sub-views
    // (Basic information / Onboarding / Nutrition Questionnaire) are a tab strip
    // inside the page, not sidebar children, so the breadcrumb reads
    // "Clients › Profile".
    id: "profile",
    label: "Profile",
    icon: ProfileIcon,
    link: "/client/profile",
  },
];

// The affiliate main tab — client management without any per-client context.
// "Clients" is a flat link: clicking a client in the list opens that client's
// workspace in a new browser tab.
export const affiliateNavigation: NavItem[] = [
  {
    id: "clients",
    label: "Clients",
    icon: PeopleIcon,
    role: 2,
    link: "/client/client-list",
  },
  {
    id: "services",
    label: "Services",
    icon: BusinessCenterIcon,
    role: 2,
    link: "/client/services",
    // No `autoExpand: true` here — Services has a route-based useEffect in
    // `PlatformSidebar` that adds it to the expanded set when the URL
    // matches `/client/services`, but `autoExpand` would also short-circuit
    // `toggleSection` on chevron clicks (see the guard there), permanently
    // blocking manual collapse since there's no route-based auto-collapse
    // counterpart.
    children: [
      {
        id: "service-builder",
        label: "Service Builder",
        link: "/client/services",
      },
      {
        id: "logic-builder",
        label: "Logic Builder",
        link: "/client/services/logic",
      },
      {
        id: "lab-builder",
        label: "Lab Builder",
        link: "/client/services/lab",
      },
      // {
      //   id: "pricing",
      //   label: "Pricing",
      //   link: "/client/services/pricing",
      // },
    ],
  },
  {
    // Replaces the old Orders page: the same two listings, merged into one
    // Assessments table, plus Programming and Prescription. Cross-client, hence
    // the `-list` suffix that `client-list` already uses — `/client/history` is
    // the single-client page in the workspace shell.
    id: "history-list",
    label: "History",
    icon: HistoryIcon,
    role: 2,
    link: "/client/history-list",
  },
  {
    id: "walkthroughs",
    label: "Walkthroughs",
    icon: WalkthroughIcon,
    role: 2,
    link: "/client/walkthroughs",
  },
  // {
  //   id: "email-campaign",
  //   label: "Email Campaign",
  //   icon: EmailIcon,
  //   role: 2,
  //   link: "/client/email-campaign",
  // },
  {
    id: "settings",
    label: "Settings",
    icon: SettingsIcon,
    role: 2,
    link: "/client/settings",
  },
];

// The customer's own workspace (role 1, /platform routes) — the same
// single-subject pages as the client workspace, minus Client info.
export const customerNavigation: NavItem[] = [
  {
    id: "customer-dashboard",
    label: "Dashboard",
    icon: GridLayoutIcon,
    role: 1,
    link: "/platform/dashboard",
  },
  {
    id: "customer-score",
    label: "Health overview",
    icon: PulseIcon,
    role: 1,
    link: "/platform/score",
  },
  {
    id: "customer-programming",
    label: "Programming",
    icon: SquareCheckIcon,
    role: 1,
    link: "/platform/programming/nutrition",
    children: [
      {
        id: "customer-nutrition",
        label: "Nutrition",
        link: "/platform/programming/nutrition",
      },
      {
        id: "customer-workout",
        label: "Workout",
        link: "/platform/programming/workout",
      },
      {
        id: "customer-bio-optimization",
        label: "Bio Optimization",
        link: "/platform/programming/bio-optimization",
      },
    ],
  },
  {
    id: "customer-history",
    label: "History",
    icon: HistoryIcon,
    role: 1,
    link: "/platform/history",
  },
  {
    // Last, mirroring the client workspace rail. Same Profile page, resolved to
    // the logged-in customer by SubjectProvider mode="self".
    id: "customer-profile",
    label: "Profile",
    icon: ProfileIcon,
    role: 1,
    link: "/platform/profile",
  },
];

// Combined tree used only for breadcrumb label/link resolution
// (`getBreadcrumbItems` above) — the sidebars render the per-shell trees.
export const navigationStructure: NavItem[] = [
  {
    ...affiliateNavigation[0],
    children: [...clientWorkspaceNavigation],
  },
  ...affiliateNavigation.slice(1),
  ...customerNavigation,
];
